import { useQuery } from "@apollo/client";
import { useFlag } from "@unleash/proxy-client-react";

import { GET_ACTIVE_ADVENT_CALENDAR } from "../graphql/resolvers/specialPack";
import { ActiveAdventCalendar } from "../types/__generated__/ActiveAdventCalendar";
import { FETCH_POLICY_NO_CACHE, UNLEASH_ADVENT_CALENDAR_LANDING_PAGE } from "./constants";

/**
 * [DEV-4580] Whether a listed special pack edition should open the advent calendar landing page
 * instead of its generic details tray/page: it is the edition that page sells, identified by the
 * page's own `getActiveAdventCalendar` query so the two never disagree.
 *
 * [DEV-4543] Only while the landing page flag is on for this visitor. While it is off, that URL
 * serves the coming-soon placeholder, which links back to the special packs page. Drop the flag
 * check together with that gate.
 *
 * `no-cache` because the Apollo cache is persisted across visits, so a cached result could keep
 * pointing at a previous visit's edition (or none). Writing it would also overlap the edition,
 * options and products the network-only special packs listing reads, which can make that listing
 * refetch. Cards that mount together still share one request through query deduplication.
 */
const useIsAdventCalendarLandingEdition = (editionSlug: string | null | undefined): boolean => {
  const isLandingPageEnabled = useFlag(UNLEASH_ADVENT_CALENDAR_LANDING_PAGE);
  const { data } = useQuery<ActiveAdventCalendar>(GET_ACTIVE_ADVENT_CALENDAR, {
    skip: !isLandingPageEnabled || !editionSlug,
    fetchPolicy: FETCH_POLICY_NO_CACHE,
  });

  return isLandingPageEnabled && !!editionSlug && data?.getActiveAdventCalendar?.slug === editionSlug;
};

export default useIsAdventCalendarLandingEdition;
